import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "header-section" }
const _hoisted_3 = { class: "flex items-center px-6 pt-16 pb-6" }
const _hoisted_4 = { class: "header-title" }
const _hoisted_5 = { class: "page-subtitle" }
const _hoisted_6 = {
  key: 0,
  class: "queue-type-indicator"
}
const _hoisted_7 = {
  key: 0,
  class: "remaining-tickets-badge"
}
const _hoisted_8 = { class: "queue-type-label" }
const _hoisted_9 = {
  key: 1,
  class: "loading-container"
}
const _hoisted_10 = {
  key: 2,
  class: "empty-state-container"
}
const _hoisted_11 = { class: "empty-state" }
const _hoisted_12 = { class: "empty-title" }
const _hoisted_13 = { class: "empty-description" }
const _hoisted_14 = {
  key: 3,
  class: "content-wrapper"
}
const _hoisted_15 = { class: "date-selection-section" }
const _hoisted_16 = { class: "section-header" }
const _hoisted_17 = { class: "section-title" }
const _hoisted_18 = { class: "date-grid" }
const _hoisted_19 = ["onClick"]
const _hoisted_20 = { class: "date-day" }
const _hoisted_21 = { class: "date-number" }
const _hoisted_22 = { class: "date-month" }
const _hoisted_23 = {
  key: 0,
  class: "status-badge sold-out-badge"
}
const _hoisted_24 = {
  key: 1,
  class: "status-badge past-badge"
}
const _hoisted_25 = {
  key: 2,
  class: "status-badge limit-badge"
}
const _hoisted_26 = {
  key: 3,
  class: "capacity-warning"
}
const _hoisted_27 = {
  key: 0,
  class: "time-selection-section"
}
const _hoisted_28 = { class: "section-header" }
const _hoisted_29 = { class: "section-title" }
const _hoisted_30 = { class: "time-slots-grid" }
const _hoisted_31 = {
  key: 0,
  class: "status-text sold-out-text"
}
const _hoisted_32 = {
  key: 1,
  class: "status-text past-text"
}
const _hoisted_33 = {
  key: 2,
  class: "status-text limit-text"
}
const _hoisted_34 = {
  key: 3,
  class: "capacity-badge"
}
const _hoisted_35 = { class: "bottom-bar" }
const _hoisted_36 = { class: "bottom-bar-content" }

import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonContent, IonButton, IonIcon, IonSpinner, IonChip, IonLabel } from '@ionic/vue';
import { chevronBack, calendar, time, star } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { anykrowdApi } from '@/api/anykrowdApi';
import useToast from '@/shared/composables/useToast';
import { useAuthStore } from '@/stores/auth/authStore';
import useQueue from '@/modules/queue/composables/useQueue';


export default /*@__PURE__*/_defineComponent({
  __name: 'QueueDateSelectionPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { presentToast } = useToast();
const authStore = useAuthStore();
const { bookEventTicketSlot } = useQueue();

const activityId = computed(() => route.params.activityId as string);
const isFromUnbooked = computed(() => route.query.from_unbooked === 'true');
const unbookedEventTicketIds = ref<number[]>([]);
const unbookedQueueTypes = ref<string[]>([]);
const isBooking = ref(false);

const isLoading = ref(false);
const schedules = ref<any[]>([]);
const selectedDate = ref<string | null>(null);
const selectedTimeSlot = ref<string | null>(null);
const bookingType = ref<'standalone' | 'event_linked'>('standalone');
const queueType = ref<'chronological' | 'time_slot'>('chronological');
const activity = ref<any>(null);
const userCurrentBookings = ref<number>(0);

// Group schedules by date
const groupedSchedules = computed(() => {
	const groups: Record<string, any[]> = {};

	schedules.value.forEach(schedule => {
		// Make sure we have a valid date
		if (schedule.date) {
			if (!groups[schedule.date]) {
				groups[schedule.date] = [];
			}
			groups[schedule.date].push(schedule);
		}
	});

	return groups;
});

// Get unique dates
const availableDates = computed(() => {
	return Object.keys(groupedSchedules.value).sort();
});

// Get time slots for selected date
const availableTimeSlots = computed(() => {
	if (!selectedDate.value) return [];

	return groupedSchedules.value[selectedDate.value] || [];
});

// Helper functions to check booking availability
const isPastDate = (dateStr: string) => {
	// Parse the date string properly (add time component to avoid timezone issues)
	const date = new Date(dateStr + 'T00:00:00');
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	date.setHours(0, 0, 0, 0);
	return date < today;
};

const isPastTimeSlot = (dateStr: string, startTimeStr: string, endTimeStr?: string) => {
	if (!startTimeStr) return false;

	// Use end time if provided (from schedule.end_time), otherwise use start time
	const timeToCheck = endTimeStr || startTimeStr;
	const [hours, minutes] = timeToCheck.split(':').map(Number);
	const slotDate = new Date(dateStr);
	slotDate.setHours(hours, minutes, 0, 0);

	// Time slot is only past if the end time has passed
	return slotDate < new Date();
};

const hasExceededBookingLimit = () => {
	// If we don't have activity data yet, don't restrict
	if (!activity.value) return false;

	// Get max bookings, default to 0 (no limit) if not set
	const maxBookings = activity.value.max_bookings_per_user || 0;

	// If 0, there's no limit
	if (maxBookings === 0) return false;

	// Check if user has reached or exceeded the limit
	return userCurrentBookings.value >= maxBookings;
};

const isSlotSoldOut = (schedule: any) => {
	// Check if slot is inactive
	if (schedule.is_active === 0 || schedule.is_active === false) return true;
	// Check if capacity is explicitly set to 0 or if available is false
	if (schedule.available === false) return true;
	if (schedule.capacity !== undefined && schedule.capacity <= 0) return true;

	// Check availability from the new availability structure
	if (schedule.availability) {
		// If booking from unbooked tickets, check availability for the specific queue type
		if (isFromUnbooked.value && unbookedQueueTypes.value.length > 0) {
			const currentQueueType = unbookedQueueTypes.value[0] || 'normal';
			const queueTypeKey = currentQueueType === 'fastlane' ? 'fastlane' : 'normal';
			const typeAvail = schedule.availability[queueTypeKey]?.available ?? 0;
			// Sold out if the specific queue type has no availability
			return typeAvail <= 0;
		}

		// Otherwise, sold out if both normal and fastlane have no available slots
		const normalAvail = schedule.availability.normal?.available ?? 0;
		const fastlaneAvail = schedule.availability.fastlane?.available ?? 0;
		if (normalAvail <= 0 && fastlaneAvail <= 0) {
			return true;
		}
	}

	return false;
};

// Check if an entire date is sold out (all time slots are sold out)
const isDateSoldOut = (date: string) => {
	const schedulesForDate = groupedSchedules.value[date] || [];

	// If no schedules for this date, consider it sold out
	if (schedulesForDate.length === 0) return true;

	// Check if ALL time slots for this date are sold out
	// Date is sold out only if every single slot has no availability
	const allSlotsSoldOut = schedulesForDate.every((schedule: any) => {
		if (schedule.is_active === 0 || schedule.is_active === false) return true;
		if (schedule.available === false) return true;
		if (schedule.capacity !== undefined && schedule.capacity <= 0) return true;

		// Check availability from the new availability structure
		if (schedule.availability) {
			// If booking from unbooked tickets, check availability for the specific queue type
			if (isFromUnbooked.value && unbookedQueueTypes.value.length > 0) {
				const currentQueueType = unbookedQueueTypes.value[0] || 'normal';
				const queueTypeKey = currentQueueType === 'fastlane' ? 'fastlane' : 'normal';
				const typeAvail = schedule.availability[queueTypeKey]?.available ?? 0;
				// Slot is sold out if the specific queue type has no availability
				return typeAvail <= 0;
			}

			// Otherwise, slot is sold out if both normal and fastlane have no availability
			const normalAvail = schedule.availability.normal?.available ?? 0;
			const fastlaneAvail = schedule.availability.fastlane?.available ?? 0;
			return normalAvail <= 0 && fastlaneAvail <= 0;
		}

		return false;
	});

	return allSlotsSoldOut;
};

// Get total available capacity across all time slots for a date
const getDateTotalCapacity = (date: string) => {
	const schedulesForDate = groupedSchedules.value[date] || [];

	// Sum up available capacity from all time slots (normal + fastlane)
	let totalAvailable = 0;
	schedulesForDate.forEach((schedule: any) => {
		if (schedule.availability) {
			const normalAvail = schedule.availability.normal?.available ?? 0;
			const fastlaneAvail = schedule.availability.fastlane?.available ?? 0;
			totalAvailable += normalAvail + fastlaneAvail;
		} else if (schedule.capacity !== undefined && schedule.capacity > 0) {
			// Fallback to schedule capacity if availability structure not present
			totalAvailable += schedule.capacity;
		}
	});

	return totalAvailable;
};

const isDateBookable = (date: string, scheduleData: any) => {
	// Check if date is in the past
	if (isPastDate(date)) return false;

	// Check if entire date is sold out (all time slots)
	if (isDateSoldOut(date)) return false;

	// Check if user has exceeded booking limit
	if (hasExceededBookingLimit()) return false;

	// Check if within advance booking hours
	if (activity.value && activity.value.advance_booking_hours > 0) {
		const bookingDate = new Date(date);
		const minBookingDate = new Date();
		minBookingDate.setHours(minBookingDate.getHours() + activity.value.advance_booking_hours);
		if (bookingDate < minBookingDate) return false;
	}

	return scheduleData.available !== false;
};

const isTimeSlotBookable = (date: string, slot: any) => {
	// Check if time slot is in the past (check end time, not start time)
	if (isPastTimeSlot(date, slot.start_time, slot.end_time)) return false;

	// Check if sold out
	if (isSlotSoldOut(slot)) return false;

	// Check if user has exceeded booking limit
	if (hasExceededBookingLimit()) return false;

	// Check if within advance booking hours
	if (activity.value && activity.value.advance_booking_hours > 0 && slot.start_time) {
		const [hours, minutes] = slot.start_time.split(':').map(Number);
		const slotDateTime = new Date(date);
		slotDateTime.setHours(hours, minutes, 0, 0);
		const minBookingDate = new Date();
		minBookingDate.setHours(minBookingDate.getHours() + activity.value.advance_booking_hours);
		if (slotDateTime < minBookingDate) return false;
	}

	return slot.available !== false;
};

// Check if continue button should be enabled
const canContinue = computed(() => {
	// Always allow continuing if date is selected (for both standalone and event-linked)
	if (!selectedDate.value) return false;

	// For time_slot mode, require time slot selection
	if (queueType.value === 'time_slot' && !selectedTimeSlot.value) {
		return false;
	}

	return true;
});

// Queue type display for unbooked tickets
const queueTypeDisplay = computed(() => {
	if (!isFromUnbooked.value || unbookedQueueTypes.value.length === 0) return null;

	// Show queue type for the CURRENT ticket being booked (first in the list)
	const currentQueueType = unbookedQueueTypes.value[0] || 'normal';

	if (currentQueueType === 'fastlane') {
		return { type: 'fastlane', label: t('queue.fastlane'), showIcon: false };
	}

	return { type: 'normal', label: t('queue.normal_queue'), showIcon: false };
});

const remainingTicketsCount = computed(() => {
	if (!isFromUnbooked.value) return 0;
	return unbookedEventTicketIds.value.length;
});

const loadSchedules = async () => {
	isLoading.value = true;

	try {
		// Fetch schedules and activity details
		const [schedulesResponse, activityResponse] = await Promise.all([
			anykrowdApi.get(`/queue/activities/${activityId.value}/schedules`, true),
			anykrowdApi.get(`/queue/activities/${activityId.value}`, true)
		]);

		bookingType.value = schedulesResponse.data.booking_type;
		queueType.value = schedulesResponse.data.queue_type;

		// Filter schedules to only include those with valid dates and that are active
		const fetchedSchedules = schedulesResponse.data.schedules || [];
		schedules.value = fetchedSchedules.filter((schedule: any) => {
			// Only include schedules that have a date and are active
			return schedule.date && (schedule.is_active === 1 || schedule.is_active === true || schedule.is_active === undefined);
		});

		activity.value = activityResponse.data;

		// Check if user booking count is provided in the activity or schedules response
		// Since the user-bookings endpoint doesn't exist, we'll look for this info in the main responses
		if (authStore.isAuthenticated()) {
			// Check if the activity response includes user booking info
			userCurrentBookings.value = activityResponse.data.user_bookings_count ||
									   schedulesResponse.data.user_bookings_count ||
									   0;
		}

		// Auto-select if only one date available
		if (schedulesResponse.data.schedules.length === 1) {
			selectedDate.value = schedulesResponse.data.schedules[0].date;
		}

		// For event-linked, auto-select the event date
		if (schedulesResponse.data.booking_type === 'event_linked' && schedulesResponse.data.schedules.length > 0) {
			selectedDate.value = schedulesResponse.data.schedules[0].date;
		}
	} catch (error: any) {
		console.error('Error loading schedules:', error);
		await presentToast('top', t('queue.error_loading_schedules'), 3000, 'danger');
		router.back();
	} finally {
		isLoading.value = false;
	}
};

const selectDate = (date: string) => {
	// Check if date is bookable
	const scheduleForDate = groupedSchedules.value[date][0];
	if (!isDateBookable(date, scheduleForDate)) {
		// Show appropriate warning message
		if (isPastDate(date)) {
			presentToast('top', t('queue.error_past_date'), 3000, 'warning');
		} else if (isDateSoldOut(date)) {
			presentToast('top', t('queue.error_sold_out'), 3000, 'warning');
		} else if (hasExceededBookingLimit()) {
			presentToast('top', t('queue.error_booking_limit_exceeded'), 3000, 'warning');
		} else {
			presentToast('top', t('queue.error_not_available'), 3000, 'warning');
		}
		return;
	}

	selectedDate.value = date;
	selectedTimeSlot.value = null; // Reset time slot when date changes

	// If chronological queue, we can proceed with just the date
	if (queueType.value === 'chronological') {
		const hasNoTimeSlots = !groupedSchedules.value[date][0].start_time;
		if (hasNoTimeSlots) {
			// Auto-select the schedule without time slot
			selectedTimeSlot.value = null;
		}
	}
};

const selectTimeSlot = (schedule: { start_time: string; end_time?: string; [key: string]: any }) => {
	// Check if time slot is bookable
	if (!selectedDate.value || !isTimeSlotBookable(selectedDate.value, schedule)) {
		// Show appropriate warning message
		if (selectedDate.value && isPastTimeSlot(selectedDate.value, schedule.start_time, schedule.end_time)) {
			presentToast('top', t('queue.error_past_time_slot'), 3000, 'warning');
		} else if (selectedDate.value && isSlotSoldOut(schedule)) {
			presentToast('top', t('queue.error_sold_out'), 3000, 'warning');
		} else if (hasExceededBookingLimit()) {
			presentToast('top', t('queue.error_booking_limit_exceeded'), 3000, 'warning');
		} else {
			presentToast('top', t('queue.error_not_available'), 3000, 'warning');
		}
		return;
	}

	selectedTimeSlot.value = `${formatTime(schedule.start_time)}-${formatTime(schedule.end_time || '')}`;
};

const formatTime = (timeStr: string) => {
	// Remove seconds from time string (HH:MM:SS -> HH:MM)
	if (!timeStr) return '';
	const parts = timeStr.split(':');
	if (parts.length >= 2) {
		return `${parts[0]}:${parts[1]}`;
	}
	return timeStr;
};

const handleBooking = async () => {
	if (!canContinue.value) return;

	if (isFromUnbooked.value && unbookedEventTicketIds.value.length > 0 && selectedDate.value) {
		// Direct booking flow for unbooked tickets - book ONE ticket at a time
		isBooking.value = true;

		try {
			// Book only the first ticket
			const eventTicketId = unbookedEventTicketIds.value[0];
			const queueType = unbookedQueueTypes.value[0] || 'normal';

			// Extract start time from selectedTimeSlot format "HH:MM-HH:MM" to "HH:MM"
			let timeSlotForApi: string | undefined = undefined;
			if (selectedTimeSlot.value) {
				// If format is "HH:MM-HH:MM", extract just the start time "HH:MM"
				if (selectedTimeSlot.value.includes('-')) {
					timeSlotForApi = selectedTimeSlot.value.split('-')[0];
				} else {
					timeSlotForApi = selectedTimeSlot.value;
				}
			}

			await bookEventTicketSlot(eventTicketId, {
				target_date: selectedDate.value,
				target_time_slot: timeSlotForApi,
				queue_type: queueType,
			});

			// Remove the booked ticket from the list
			const remainingTicketIds = unbookedEventTicketIds.value.slice(1);
			const remainingQueueTypes = unbookedQueueTypes.value.slice(1);

			// Clear stored data if no more tickets
			if (remainingTicketIds.length === 0) {
				sessionStorage.removeItem('unbooked_event_ticket_ids');
				sessionStorage.removeItem('unbooked_queue_types');
				sessionStorage.removeItem('booking_event_ticket_ids');

				await presentToast('top', t('queue.all_tickets_booked_successfully'), 3000, 'success');
				router.push('/my-queues');
			} else {
				// Update session storage with remaining tickets
				sessionStorage.setItem('unbooked_event_ticket_ids', JSON.stringify(remainingTicketIds));
				sessionStorage.setItem('unbooked_queue_types', JSON.stringify(remainingQueueTypes));

				// Update local state
				unbookedEventTicketIds.value = remainingTicketIds;
				unbookedQueueTypes.value = remainingQueueTypes;

				// Reset date/time selection for next ticket
				selectedDate.value = null;
				selectedTimeSlot.value = null;

				// Show success message and indicate remaining tickets
				const remainingCount = remainingTicketIds.length;
				if (remainingCount > 0) {
					await presentToast('top', t('queue.ticket_booked_continue_with_remaining', { count: remainingCount }), 3000, 'success');
				}
			}
		} catch (error: any) {
			console.error('Error during booking:', error);
			// Show error message - don't show success message on error
			const errorMessage = error?.response?.data?.message || error?.message || t('queue.booking_failed');
			await presentToast('top', errorMessage, 5000, 'danger');
			// Don't update state or show success message on error
		} finally {
			isBooking.value = false;
		}

		return;
	}

	// Regular flow: navigate to ticket selection
	if (isFromUnbooked.value && unbookedEventTicketIds.value.length > 0) {
		sessionStorage.setItem('booking_event_ticket_ids', JSON.stringify(unbookedEventTicketIds.value));
	}

	router.push({
		name: 'queue-tickets-buy',
		params: {
			activityId: activityId.value,
		},
		query: {
			date: selectedDate.value,
			timeSlot: selectedTimeSlot.value || undefined,
			from_unbooked: isFromUnbooked.value ? 'true' : undefined,
		}
	});
};

onMounted(() => {
	// Load unbooked event ticket IDs if coming from unbooked flow
	if (isFromUnbooked.value) {
		const storedIds = sessionStorage.getItem('unbooked_event_ticket_ids');
		if (storedIds) {
			try {
				unbookedEventTicketIds.value = JSON.parse(storedIds);
			} catch (e) {
				console.error('Error parsing unbooked event ticket IDs:', e);
			}
		}

		// Load queue types
		const storedQueueTypes = sessionStorage.getItem('unbooked_queue_types');
		if (storedQueueTypes) {
			try {
				unbookedQueueTypes.value = JSON.parse(storedQueueTypes);
			} catch (e) {
				console.error('Error parsing unbooked queue types:', e);
			}
		}
	}
	loadSchedules();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), { class: "queue-date-selection" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(IonButton), {
              fill: "clear",
              class: "back-button",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).back()))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(IonIcon), {
                  icon: _unref(chevronBack),
                  class: "back-icon"
                }, null, 8, ["icon"])
              ]),
              _: 1
            }),
            _createElementVNode("h1", _hoisted_4, _toDisplayString(_unref(t)('queue.select_date_time')), 1)
          ]),
          _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)('queue.choose_preferred_slot')), 1)
        ]),
        (isFromUnbooked.value && queueTypeDisplay.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              (remainingTicketsCount.value > 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_unref(t)('queue.booking_ticket_of_total', { current: 1, total: remainingTicketsCount.value })), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                class: _normalizeClass(["queue-type-badge", `queue-type-${queueTypeDisplay.value.type}`])
              }, [
                (queueTypeDisplay.value.showIcon)
                  ? (_openBlock(), _createBlock(_unref(IonIcon), {
                      key: 0,
                      icon: _unref(star),
                      class: "queue-type-icon"
                    }, null, 8, ["icon"]))
                  : _createCommentVNode("", true),
                _createElementVNode("span", _hoisted_8, _toDisplayString(queueTypeDisplay.value.label), 1)
              ], 2)
            ]))
          : _createCommentVNode("", true),
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createVNode(_unref(IonSpinner), { name: "dots" })
            ]))
          : (schedules.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("div", _hoisted_11, [
                  _cache[2] || (_cache[2] = _createElementVNode("div", { class: "empty-icon" }, "??", -1)),
                  _createElementVNode("h2", _hoisted_12, _toDisplayString(_unref(t)('queue.no_schedules_available')), 1),
                  _createElementVNode("p", _hoisted_13, _toDisplayString(_unref(t)('queue.no_schedules_description')), 1),
                  _createVNode(_unref(IonButton), {
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).back())),
                    class: "mt-4"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('general.go_back')), 1)
                    ]),
                    _: 1
                  })
                ])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _createVNode(_unref(IonIcon), {
                      icon: _unref(calendar),
                      class: "section-icon"
                    }, null, 8, ["icon"]),
                    _createElementVNode("h2", _hoisted_17, _toDisplayString(_unref(t)('queue.select_date')), 1)
                  ]),
                  _createElementVNode("div", _hoisted_18, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableDates.value, (date) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: date,
                        class: _normalizeClass(["date-card", {
								'selected': selectedDate.value === date,
								'disabled': !isDateBookable(date, groupedSchedules.value[date][0]),
								'past': isPastDate(date),
								'sold-out': isDateSoldOut(date),
								'limit-exceeded': hasExceededBookingLimit() && !isPastDate(date) && !isDateSoldOut(date)
							}]),
                        onClick: ($event: any) => (selectDate(date))
                      }, [
                        _createElementVNode("div", _hoisted_20, _toDisplayString(new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })), 1),
                        _createElementVNode("div", _hoisted_21, _toDisplayString(new Date(date + 'T00:00:00').getDate()), 1),
                        _createElementVNode("div", _hoisted_22, _toDisplayString(new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })), 1),
                        (isDateSoldOut(date))
                          ? (_openBlock(), _createElementBlock("div", _hoisted_23, _toDisplayString(_unref(t)('queue.sold_out')), 1))
                          : (isPastDate(date))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(_unref(t)('queue.past')), 1))
                            : (hasExceededBookingLimit())
                              ? (_openBlock(), _createElementBlock("div", _hoisted_25, _toDisplayString(_unref(t)('queue.limit_reached')), 1))
                              : (!isDateSoldOut(date) && getDateTotalCapacity(date) <= 5 && getDateTotalCapacity(date) > 0)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_26, _toDisplayString(getDateTotalCapacity(date)) + " " + _toDisplayString(_unref(t)('queue.spots_left')), 1))
                                : _createCommentVNode("", true)
                      ], 10, _hoisted_19))
                    }), 128))
                  ])
                ]),
                (selectedDate.value && availableTimeSlots.value.length > 0 && availableTimeSlots.value[0].start_time)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                      _createElementVNode("div", _hoisted_28, [
                        _createVNode(_unref(IonIcon), {
                          icon: _unref(time),
                          class: "section-icon"
                        }, null, 8, ["icon"]),
                        _createElementVNode("h2", _hoisted_29, _toDisplayString(_unref(t)('queue.select_time_slot')), 1)
                      ]),
                      _createElementVNode("div", _hoisted_30, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableTimeSlots.value, (slot) => {
                          return (_openBlock(), _createBlock(_unref(IonChip), {
                            key: `${slot.start_time}-${slot.end_time}`,
                            class: _normalizeClass({
								'selected': selectedTimeSlot.value === `${formatTime(slot.start_time)}-${formatTime(slot.end_time)}`,
								'disabled': !isTimeSlotBookable(selectedDate.value, slot),
								'past': isPastTimeSlot(selectedDate.value, slot.start_time, slot.end_time),
								'sold-out': isSlotSoldOut(slot),
								'limit-exceeded': hasExceededBookingLimit() && !isPastTimeSlot(selectedDate.value, slot.start_time, slot.end_time) && !isSlotSoldOut(slot)
							}),
                            onClick: ($event: any) => (selectTimeSlot(slot))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(IonLabel), null, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(formatTime(slot.start_time)) + " - " + _toDisplayString(formatTime(slot.end_time)) + " ", 1),
                                  (isSlotSoldOut(slot))
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_31, _toDisplayString(_unref(t)('queue.sold_out')), 1))
                                    : (isPastTimeSlot(selectedDate.value, slot.start_time, slot.end_time))
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_32, _toDisplayString(_unref(t)('queue.past')), 1))
                                      : (hasExceededBookingLimit())
                                        ? (_openBlock(), _createElementBlock("span", _hoisted_33, _toDisplayString(_unref(t)('queue.limit_reached')), 1))
                                        : (slot.capacity <= 5 && slot.capacity > 0)
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_34, " (" + _toDisplayString(slot.capacity) + ") ", 1))
                                          : _createCommentVNode("", true)
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1032, ["class", "onClick"]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_35, [
                  _cache[3] || (_cache[3] = _createElementVNode("div", { class: "bottom-bar-backdrop" }, null, -1)),
                  _createElementVNode("div", _hoisted_36, [
                    _createVNode(_unref(IonButton), {
                      expand: "block",
                      disabled: !canContinue.value || isBooking.value,
                      onClick: handleBooking,
                      class: "continue-button"
                    }, {
                      default: _withCtx(() => [
                        (isBooking.value)
                          ? (_openBlock(), _createBlock(_unref(IonSpinner), {
                              key: 0,
                              name: "dots",
                              class: "mr-2"
                            }))
                          : _createCommentVNode("", true),
                        _createTextVNode(" " + _toDisplayString(isFromUnbooked.value ? _unref(t)('queue.book_now') : _unref(t)('queue.continue_to_tickets')), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"])
                  ])
                ])
              ]))
      ])
    ]),
    _: 1
  }))
}
}

})